import React, { useCallback, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { createEditor, Editor, Transforms, Text, Element as SlateElement, Range, Node } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import './RichTextEditor.css';

// Define custom element types
const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const BLOCK_TYPES = ['block-quote', 'code-block'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

// Helper functions for formatting
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }

  // Force re-render to show active state immediately
  ReactEditor.focus(editor);
};

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  );

  return !!match;
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

// Component for rendering formatted elements
const CustomElement = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };

  switch (element.type) {
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'code-block':
      return (
        <pre {...attributes}>
          <code>{children}</code>
        </pre>
      );
    case 'block-quote':
      return (
        <blockquote className="editor-blockquote" style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case 'link':
      return (
        <a {...attributes} href={element.url} target="_blank" rel="noopener noreferrer" className="editor-link">
          {children}
        </a>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

// Component for rendering formatted text (bold, italic, etc.)
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  if (leaf.strikethrough) {
    children = <s>{children}</s>;
  }

  if (leaf.code) {
    children = <code className="inline-code">{children}</code>;
  }

  // Render URLs as clickable links
  if (leaf.url) {
    children = (
      <a
        href={leaf.url}
        target="_blank"
        rel="noopener noreferrer"
        className="editor-link"
        onClick={(e) => {
          // Allow clicking the link
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Prevent editor from losing focus when clicking link
          e.preventDefault();
          window.open(leaf.url, '_blank', 'noopener,noreferrer');
        }}
      >
        {children}
      </a>
    );
  }

  return <span {...attributes}>{children}</span>;
};

// Function to detect and decorate URLs
const decorateUrl = ([node, path]) => {
  const ranges = [];

  if (!Text.isText(node)) {
    return ranges;
  }

  const { text } = node;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  let match;

  while ((match = urlRegex.exec(text)) !== null) {
    ranges.push({
      anchor: { path, offset: match.index },
      focus: { path, offset: match.index + match[0].length },
      url: match[0],
    });
  }

  return ranges;
};

// Helper function to check if selection has a link
const isLinkActive = (editor) => {
  const [link] = Editor.nodes(editor, {
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
  return !!link;
};

// Helper function to unwrap a link
const unwrapLink = (editor) => {
  Transforms.unwrapNodes(editor, {
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'link',
  });
};

// Helper function to wrap selection in a link
const wrapLink = (editor, url, text) => {
  if (isLinkActive(editor)) {
    unwrapLink(editor);
  }

  const { selection } = editor;
  const isCollapsed = selection && Range.isCollapsed(selection);

  const link = {
    type: 'link',
    url,
    children: [{ text: text || url }],
  };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    // If there's a selection and custom text provided, replace with custom text
    if (text) {
      Transforms.delete(editor);
      Transforms.insertNodes(editor, link);
    } else {
      Transforms.wrapNodes(editor, link, { split: true });
      Transforms.collapse(editor, { edge: 'end' });
    }
  }
};

// Helper function to insert a link (called after URL is entered in modal)
const insertLinkWithUrl = (editor, url, text, savedSelection) => {
  if (!url) return false;

  // Validate URL
  try {
    new URL(url);
  } catch {
    return false;
  }

  // Restore selection if saved
  if (savedSelection) {
    Transforms.select(editor, savedSelection);
  }

  wrapLink(editor, url, text);
  return true;
};

// Toolbar button component
const ToolbarButton = ({ format, icon, isActive, onMouseDown, title }) => {
  return (
    <button
      type="button"
      className={`toolbar-button ${isActive ? 'active' : ''}`}
      onMouseDown={onMouseDown}
      title={title || format}
    >
      {icon}
    </button>
  );
};

const RichTextEditor = forwardRef(({ value, onChange, onSend, placeholder, onKeyDown, convertEmoticons, enableFormatting = false, users = {}, onMentionSearch, showMentions = false, mentions = [], selectedMentionIndex = 0, onSelectMention, onPasteImage }, ref) => {
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [savedSelection, setSavedSelection] = useState(null);
  const [editorValue, setEditorValue] = useState(() => {
    // Ensure initial value is always valid
    if (value && Array.isArray(value) && value.length > 0) {
      return value;
    }
    return [
      {
        type: 'paragraph',
        children: [{ text: '' }],
      },
    ];
  });
  const [, setForceUpdate] = useState(0);

  const renderElement = useCallback(props => <CustomElement {...props} />, []);
  const renderLeaf = useCallback(props => <Leaf {...props} />, []);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    insertEmoji: (emoji) => {
      Transforms.insertText(editor, emoji);
      ReactEditor.focus(editor);
    },
    insertMention: (userName) => {
      const { selection } = editor;
      if (!selection) return;

      try {
        const [node] = Editor.node(editor, selection);
        if (Text.isText(node)) {
          const text = node.text;
          const cursorOffset = selection.anchor.offset;
          const textBeforeCursor = text.slice(0, cursorOffset);
          const lastAtIndex = textBeforeCursor.lastIndexOf('@');

          if (lastAtIndex !== -1) {
            // Delete from @ to cursor
            const deleteCount = cursorOffset - lastAtIndex;
            Transforms.delete(editor, {
              distance: deleteCount,
              unit: 'character',
              reverse: true,
            });

            // Insert the mention text
            Transforms.insertText(editor, `@${userName} `);
          }
        }
      } catch (error) {
        console.error('Error inserting mention:', error);
      }

      ReactEditor.focus(editor);
    },
    clear: () => {
      // Reset to initial empty state first
      const emptyValue = [
        {
          type: 'paragraph',
          children: [{ text: '' }],
        },
      ];

      // Select all and delete
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });

      // Remove all nodes and insert empty paragraph
      editor.children = emptyValue;
      editor.selection = {
        anchor: { path: [0, 0], offset: 0 },
        focus: { path: [0, 0], offset: 0 },
      };
      editor.history = { undos: [], redos: [] };
      editor.onChange();

      // Reset all formatting marks
      Editor.removeMark(editor, 'bold');
      Editor.removeMark(editor, 'italic');
      Editor.removeMark(editor, 'underline');
      Editor.removeMark(editor, 'strikethrough');
      Editor.removeMark(editor, 'code');

      setEditorValue(emptyValue);

      // Force toolbar to update
      setForceUpdate(n => n + 1);

      // Notify parent of the change
      if (onChange) {
        onChange(emptyValue);
      }
    },
  }), [editor, onChange, setForceUpdate]);

  const handleChange = useCallback((newValue) => {
    setEditorValue(newValue);

    // Auto-remove code formatting when text becomes empty
    const { selection } = editor;
    if (selection) {
      try {
        const [node] = Editor.node(editor, selection);
        if (Text.isText(node) && node.text === '' && node.code) {
          // Remove code mark when text is empty
          Editor.removeMark(editor, 'code');
        }

        // Detect @ mentions
        if (onMentionSearch && Text.isText(node)) {
          const text = node.text;
          const cursorOffset = selection.anchor.offset;
          const textBeforeCursor = text.slice(0, cursorOffset);
          const lastAtIndex = textBeforeCursor.lastIndexOf('@');

          if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
            // Check if there's no space after @ (still typing the mention)
            if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
              onMentionSearch(textAfterAt, true);
            } else {
              onMentionSearch('', false);
            }
          } else {
            onMentionSearch('', false);
          }
        }
      } catch (error) {
        // Ignore errors
      }
    }

    if (onChange) {
      onChange(newValue);
    }
  }, [onChange, editor, onMentionSearch]);

  const handleBeforeInput = useCallback((event) => {
    if (!event.data) return;

    try {
      const { selection } = editor;

      // Check if we have a valid selection
      if (!selection || !Range.isCollapsed(selection)) return;

      // Get the node at the selection
      const nodeEntry = Editor.node(editor, selection);
      if (!nodeEntry) return;

      const [node, path] = nodeEntry;

      // Make sure we have a valid text node with content
      if (!Text.isText(node)) return;

      // Get the text that will be in the editor after this character is typed
      const currentText = node.text || '';
      const cursorPos = selection.anchor.offset;
      const textAfterInput = currentText.substring(0, cursorPos) + event.data + currentText.substring(cursorPos);

      // Only handle markdown shortcuts if formatting is enabled
      if (enableFormatting) {
        // Check for code block markdown (```)
        if (event.data === '`' && textAfterInput.endsWith('```')) {
          event.preventDefault();
          Editor.withoutNormalizing(editor, () => {
            // Delete the three backticks
            Transforms.delete(editor, {
              distance: 2,
              unit: 'character',
              reverse: true,
            });
            // Toggle code block
            toggleBlock(editor, 'code-block');
          });
          return;
        }

        // Check for inline code markdown (``)
        if (event.data === '`' && textAfterInput.endsWith('``') && !textAfterInput.endsWith('```')) {
          event.preventDefault();
          Editor.withoutNormalizing(editor, () => {
            // Delete the two backticks
            Transforms.delete(editor, {
              distance: 1,
              unit: 'character',
              reverse: true,
            });
            // Toggle inline code
            toggleMark(editor, 'code');
          });
          return;
        }
      }

      // Emoticon conversion
      if (convertEmoticons) {
        const emoticons = {
          ':)': '😊',
          ':-)': '😊',
          ':(': '😢',
          ':-(': '😢',
          ':D': '😀',
          ':-D': '😀',
          ';)': '😉',
          ';-)': '😉',
          ':P': '😛',
          ':-P': '😛',
          ':p': '😛',
          ':-p': '😛',
          ':o': '😮',
          ':O': '😮',
          ':-o': '😮',
          ':-O': '😮',
          ':|': '😐',
          ':-|': '😐',
          ':/': '😕',
          ':-/': '😕',
          ':*': '😘',
          ':-*': '😘',
          '<3': '❤️',
          '</3': '💔',
          '^_^': '😊',
          '-_-': '😑',
          'o_O': '😳',
          'O_o': '😳',
          '>_<': '😣',
          'T_T': '😭',
          'XD': '😆',
          'xD': '😆',
        };

        // Check if text ends with any emoticon
        for (const [emoticon, emoji] of Object.entries(emoticons)) {
          if (textAfterInput.endsWith(emoticon)) {
            event.preventDefault();

            // Delete the emoticon characters that are already there
            Editor.withoutNormalizing(editor, () => {
              if (emoticon.length > 1) {
                Transforms.delete(editor, {
                  distance: emoticon.length - 1,
                  unit: 'character',
                  reverse: true,
                });
              }
              // Insert the emoji
              Transforms.insertText(editor, emoji);
            });
            return;
          }
        }
      }
    } catch (error) {
      // Silently fail if there's an error - don't prevent default behavior
      console.error('Error in handleBeforeInput:', error);
    }
  }, [editor, convertEmoticons, enableFormatting]);

  const handlePaste = useCallback((event) => {
    // Check if clipboard contains files (images)
    const clipboardData = event.clipboardData || event.originalEvent?.clipboardData;
    if (!clipboardData) return;

    const items = clipboardData.items;
    if (!items) return;

    // Look for image files
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf('image') !== -1) {
        event.preventDefault();
        const file = item.getAsFile();

        if (file && onPasteImage) {
          onPasteImage(file);
        }
        return;
      }
    }
  }, [onPasteImage]);

  const handleKeyDown = useCallback((event) => {
    // Handle mention selection
    if (showMentions && mentions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (onSelectMention) {
          onSelectMention('down');
        }
        return;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (onSelectMention) {
          onSelectMention('up');
        }
        return;
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        if (onSelectMention) {
          onSelectMention('select');
        }
        return;
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (onMentionSearch) {
          onMentionSearch('', false);
        }
        return;
      }
    }

    // Handle markdown shortcuts only if formatting is enabled
    if (enableFormatting) {
      if (event.key === 'b' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleMark(editor, 'bold');
        return;
      }

      if (event.key === 'i' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleMark(editor, 'italic');
        return;
      }

      if (event.key === 'u' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleMark(editor, 'underline');
        return;
      }

      if (event.key === '`' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleMark(editor, 'code');
        return;
      }
    }

    // Handle Backspace at start of list item to remove list formatting
    if (event.key === 'Backspace') {
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const [match] = Editor.nodes(editor, {
          match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === 'list-item',
        });

        if (match) {
          const [, path] = match;
          const start = Editor.start(editor, path);

          // If cursor is at the start of a list item
          if (Range.equals(selection, { anchor: start, focus: start })) {
            event.preventDefault();

            // Unwrap list item and list wrapper
            Transforms.unwrapNodes(editor, {
              match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
              split: true,
            });
            Transforms.setNodes(editor, { type: 'paragraph' });
            return;
          }
        }
      }
    }

    // Handle Delete key to remove entire list when selected
    if (event.key === 'Delete') {
      const { selection } = editor;
      if (selection && !Range.isCollapsed(selection)) {
        const [match] = Editor.nodes(editor, {
          match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
        });

        if (match) {
          event.preventDefault();
          Transforms.removeNodes(editor, {
            match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
          });
          return;
        }
      }
    }

    // Handle Ctrl+Enter to send message
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (onSend) {
        onSend();
      }
      return;
    }
    // Enter and Shift+Enter create line breaks (default Slate behavior)

    // Pass through to parent handler
    if (onKeyDown) {
      onKeyDown(event);
    }
  }, [editor, onSend, onKeyDown, enableFormatting, showMentions, mentions, onSelectMention, onMentionSearch]);

  return (
    <div className="rich-text-editor">
      <Slate editor={editor} initialValue={editorValue} onChange={handleChange}>
        {enableFormatting && (
          <div className="formatting-toolbar">
            <ToolbarButton
              format="bold"
              icon={<strong>B</strong>}
              isActive={isMarkActive(editor, 'bold')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleMark(editor, 'bold');
                setForceUpdate(n => n + 1);
              }}
            />
            <ToolbarButton
              format="italic"
              icon={<em>I</em>}
              isActive={isMarkActive(editor, 'italic')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleMark(editor, 'italic');
                setForceUpdate(n => n + 1);
              }}
            />
            <ToolbarButton
              format="strikethrough"
              icon={<s>S</s>}
              isActive={isMarkActive(editor, 'strikethrough')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleMark(editor, 'strikethrough');
                setForceUpdate(n => n + 1);
              }}
            />
            <div className="toolbar-divider" />
            <ToolbarButton
              format="code"
              icon={<span>&lt;/&gt;</span>}
              isActive={isMarkActive(editor, 'code')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleMark(editor, 'code');
                setForceUpdate(n => n + 1);
              }}
            />
            <div className="toolbar-divider" />
            <ToolbarButton
              format="bulleted-list"
              icon={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <circle cx="2" cy="3" r="1.5"/>
                  <rect x="5" y="2" width="10" height="2" rx="1"/>
                  <circle cx="2" cy="8" r="1.5"/>
                  <rect x="5" y="7" width="10" height="2" rx="1"/>
                  <circle cx="2" cy="13" r="1.5"/>
                  <rect x="5" y="12" width="10" height="2" rx="1"/>
                </svg>
              }
              isActive={isBlockActive(editor, 'bulleted-list')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleBlock(editor, 'bulleted-list');
                setForceUpdate(n => n + 1);
              }}
            />
            <ToolbarButton
              format="numbered-list"
              icon={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <text x="0" y="4" fontSize="5" fontWeight="bold">1</text>
                  <rect x="5" y="2" width="10" height="2" rx="1"/>
                  <text x="0" y="9" fontSize="5" fontWeight="bold">2</text>
                  <rect x="5" y="7" width="10" height="2" rx="1"/>
                  <text x="0" y="14" fontSize="5" fontWeight="bold">3</text>
                  <rect x="5" y="12" width="10" height="2" rx="1"/>
                </svg>
              }
              isActive={isBlockActive(editor, 'numbered-list')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleBlock(editor, 'numbered-list');
                setForceUpdate(n => n + 1);
              }}
            />
            <ToolbarButton
              format="code-block"
              icon={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M5.5 3.5L2 8l3.5 4.5M10.5 3.5L14 8l-3.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
                </svg>
              }
              isActive={isBlockActive(editor, 'code-block')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleBlock(editor, 'code-block');
                setForceUpdate(n => n + 1);
              }}
            />
            <div className="toolbar-divider" />
            <ToolbarButton
              format="block-quote"
              title="Quote"
              icon={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M3 3h10v1H3V3zm0 3h10v1H3V6zm0 3h10v1H3V9zm0 3h7v1H3v-1z" />
                  <rect x="1" y="3" width="1" height="10" fill="currentColor" />
                </svg>
              }
              isActive={isBlockActive(editor, 'block-quote')}
              onMouseDown={(e) => {
                e.preventDefault();
                toggleBlock(editor, 'block-quote');
                setForceUpdate(n => n + 1);
              }}
            />
            <ToolbarButton
              format="link"
              title="Insert Link"
              icon={
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M6.354 5.5H7a.5.5 0 0 0 0-1H6.354a4.354 4.354 0 1 0 0 8.708H7a.5.5 0 0 0 0-1H6.354a3.354 3.354 0 0 1 0-6.708zM9 4.5a.5.5 0 0 0 0 1h.646a3.354 3.354 0 0 1 0 6.708H9a.5.5 0 0 0 0 1h.646a4.354 4.354 0 0 0 0-8.708H9z"/>
                  <path d="M5.5 8a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 0-1H6a.5.5 0 0 0-.5.5z"/>
                </svg>
              }
              isActive={isLinkActive(editor)}
              onMouseDown={(e) => {
                e.preventDefault();
                // Save selection before modal opens
                const { selection } = editor;
                setSavedSelection(selection ? { ...selection } : null);

                if (selection && !Range.isCollapsed(selection)) {
                  const selectedText = Editor.string(editor, selection);
                  setLinkText(selectedText);
                } else {
                  setLinkText('');
                }
                setLinkUrl('');
                setShowLinkModal(true);
              }}
            />
          </div>
        )}

        {/* Link Modal */}
        {showLinkModal && (
          <div className="link-modal-overlay" onClick={() => setShowLinkModal(false)}>
            <div className="link-modal" onClick={(e) => e.stopPropagation()}>
              <div className="link-modal-header">Insert Link</div>
              <div className="link-modal-field">
                <label className="link-modal-label">Text</label>
                <input
                  type="text"
                  className="link-modal-input"
                  placeholder="Link text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowLinkModal(false);
                    }
                  }}
                  autoFocus={!savedSelection || (savedSelection && Range.isCollapsed(savedSelection))}
                />
              </div>
              <div className="link-modal-field">
                <label className="link-modal-label">URL</label>
                <input
                  type="url"
                  className="link-modal-input"
                  placeholder="https://example.com"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (insertLinkWithUrl(editor, linkUrl, linkText, savedSelection)) {
                        setShowLinkModal(false);
                        setLinkUrl('');
                        setLinkText('');
                        setSavedSelection(null);
                        ReactEditor.focus(editor);
                      }
                    } else if (e.key === 'Escape') {
                      setShowLinkModal(false);
                    }
                  }}
                  autoFocus={savedSelection && !Range.isCollapsed(savedSelection)}
                />
              </div>
              <div className="link-modal-buttons">
                <button
                  type="button"
                  className="link-modal-cancel"
                  onClick={() => {
                    setShowLinkModal(false);
                    setLinkUrl('');
                    setLinkText('');
                    setSavedSelection(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="link-modal-submit"
                  onClick={() => {
                    if (insertLinkWithUrl(editor, linkUrl, linkText, savedSelection)) {
                      setShowLinkModal(false);
                      setLinkUrl('');
                      setLinkText('');
                      setSavedSelection(null);
                      ReactEditor.focus(editor);
                    }
                  }}
                >
                  Insert
                </button>
              </div>
            </div>
          </div>
        )}
        <Editable
          className="rich-text-input"
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          decorate={decorateUrl}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          onDOMBeforeInput={handleBeforeInput}
          onPaste={handlePaste}
          spellCheck
        />
      </Slate>
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
